import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Container,
    TextField,
    Button,
    MenuItem,
    Alert,
    CircularProgress,
    Typography,
    Dialog,
    DialogContent,
    DialogActions,
    DialogTitle,
    IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import axios from "axios";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import PageHeaderWithBreadcrumbs from "../components/common/PageHeaderWithBreadcrumbs.js";
import PageTitle from "../components/common/PageTitle";
import Space from "../components/common/Space.js";

const ResendConfirmation = () => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        passportNumber: "",
        passportCountry: "",
        birthDay: "",
        birthMonth: "",
        birthYear: "",
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "" });
    const [countries, setCountries] = useState([]);
    const [countriesLoading, setCountriesLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [openCountryDialog, setOpenCountryDialog] = useState(false);

    // Refs for auto-focus
    const monthRef = useRef(null);
    const yearRef = useRef(null);

    const handleOpenDialog = () => setOpenDialog(true);
    const handleCloseDialog = () => setOpenDialog(false);
    const handleOpenCountryDialog = () => setOpenCountryDialog(true);
    const handleCloseCountryDialog = () => setOpenCountryDialog(false);

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });

        // Fetch countries list
        const fetchCountries = async () => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_API_BASE_URL}/api/getalldata`
                );
                // console.log("API Response:", response.data);

                // Check different possible locations for country data
                let countryData = null;

                if (response.data.allcountry && Array.isArray(response.data.allcountry)) {
                    countryData = response.data.allcountry;
                } else if (response.data.body && response.data.body.allcountry) {
                    countryData = response.data.body.allcountry;
                } else if (Array.isArray(response.data)) {
                    countryData = response.data;
                }

                // console.log("Extracted country data:", countryData);

                if (countryData && Array.isArray(countryData)) {
                    // console.log("Setting countries:", countryData);
                    setCountries(countryData);
                } else {
                    console.log("No country data found in response. Full response:", response.data);
                }
            } catch (error) {
                console.error("Error fetching countries:", error);
            } finally {
                setCountriesLoading(false);
            }
        };

        fetchCountries();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        let finalValue = value;

        // Limit passport number to 9 characters and allow only English letters and numbers
        if (name === 'passportNumber') {
            if (value.length > 9) {
                return;
            }
            // Only allow English letters and numbers
            if (value && !/^[a-zA-Z0-9]*$/.test(value)) {
                return;
            }
            finalValue = value.toUpperCase();
        }

        setFormData((prev) => ({
            ...prev,
            [name]: finalValue,
        }));
        // Clear error for this field
        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: "",
            }));
        }
    };

    const handleDateChange = (e, nextRef) => {
        const { name, value } = e.target;

        // Only allow numbers
        const numericValue = value.replace(/\D/g, '');

        // Limit to 2 digits for day/month, 4 for year
        let maxLength = 2;
        if (name === 'birthYear') maxLength = 4;

        const limitedValue = numericValue.slice(0, maxLength);

        setFormData((prev) => ({
            ...prev,
            [name]: limitedValue,
        }));

        // Clear error for this specific field
        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: "",
            }));
        }

        // Validate the value in real-time
        if (limitedValue.length === maxLength) {
            const numValue = parseInt(limitedValue);
            let errorMsg = "";

            if (name === 'birthDay' && (numValue < 1 || numValue > 31)) {
                errorMsg = "resendConfirmation.form.invalidDay";
            } else if (name === 'birthMonth' && (numValue < 1 || numValue > 12)) {
                errorMsg = "resendConfirmation.form.invalidMonth";
            } else if (name === 'birthYear' && (numValue < 1900 || numValue > new Date().getFullYear())) {
                errorMsg = "resendConfirmation.form.invalidYear";
            }

            if (errorMsg) {
                setErrors((prev) => ({
                    ...prev,
                    [name]: errorMsg,
                }));
            } else if (nextRef) {
                // Only auto-focus if value is valid
                nextRef.current?.focus();
            }
        } else if (limitedValue.length === maxLength && nextRef) {
            // Auto-focus to next field when current field is filled
            nextRef.current?.focus();
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.passportNumber.trim()) {
            newErrors.passportNumber = "resendConfirmation.form.requiredField";
        } else if (formData.passportNumber.length > 9) {
            newErrors.passportNumber = "resendConfirmation.form.invalidPassportLength";
        } else if (formData.passportNumber.length < 5) {
            newErrors.passportNumber = "resendConfirmation.form.invalidPassport";
        }

        if (!formData.passportCountry) {
            newErrors.passportCountry = "resendConfirmation.form.requiredField";
        }

        if (!formData.birthDay) {
            newErrors.birthDay = "resendConfirmation.form.requiredField";
        } else {
            const day = parseInt(formData.birthDay);
            if (day < 1 || day > 31) {
                newErrors.birthDay = "resendConfirmation.form.invalidDay";
            }
        }

        if (!formData.birthMonth) {
            newErrors.birthMonth = "resendConfirmation.form.requiredField";
        } else {
            const month = parseInt(formData.birthMonth);
            if (month < 1 || month > 12) {
                newErrors.birthMonth = "resendConfirmation.form.invalidMonth";
            }
        }

        if (!formData.birthYear) {
            newErrors.birthYear = "resendConfirmation.form.requiredField";
        } else {
            const year = parseInt(formData.birthYear);
            if (year < 1900 || year > new Date().getFullYear()) {
                newErrors.birthYear = "resendConfirmation.form.invalidYear";
            }
        }



        // Check if the complete birth date is in the future
        if (formData.birthDay && formData.birthMonth && formData.birthYear) {
            const birthDate = new Date(formData.birthYear, parseInt(formData.birthMonth) - 1, parseInt(formData.birthDay));
            const today = new Date();
            today.setHours(0, 0, 0, 0);



            if (birthDate > today) {
                newErrors.birthDate = "resendConfirmation.form.futureDateError";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const isFormValid = () => {
        // Check if all fields are filled
        if (!formData.passportNumber.trim() ||
            formData.passportNumber.length > 9 ||
            !formData.passportCountry ||
            !formData.birthDay ||
            !formData.birthMonth ||
            !formData.birthYear) {
            return false;
        }

        // Validate date values
        const day = parseInt(formData.birthDay);
        const month = parseInt(formData.birthMonth);
        const year = parseInt(formData.birthYear);

        if (day < 1 || day > 31) return false;
        if (month < 1 || month > 12) return false;
        if (year < 1900 || year > new Date().getFullYear()) return false;
        if (formData.birthYear.length !== 4) return false;

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage({ type: "", text: "" });

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            // Format birth date as YYYY-MM-DD
            const birthDate = `${formData.birthDay.padStart(2, '0')}/${formData.birthMonth.padStart(2, '0')}/${formData.birthYear.padStart(2, '0')}`;

            const response = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/validateAndResendEmail`,
                {
                    passportNum: formData.passportNumber,
                    passportCountryCode: formData.passportCountry,
                    birthDate: birthDate,
                }
            );

            console.log(response);
            if (response.status === 200) {
                if (response.data === false) {
                    setMessage({
                        type: "error",
                        text: "resendConfirmation.messages.notFound",
                    });
                    // Reset form
                    setFormData({
                        passportNumber: "",
                        passportCountry: "",
                        birthDay: "",
                        birthMonth: "",
                        birthYear: "",
                    });
                } else {
                    setMessage({
                        type: "success",
                        text: "resendConfirmation.messages.success",
                    });
                    setFormData({
                        passportNumber: "",
                        passportCountry: "",
                        birthDay: "",
                        birthMonth: "",
                        birthYear: "",
                    });
                }
            }
        } catch (error) {
            console.error("Error resending email:", error);
            if (error.response?.status === 404) {
                setMessage({
                    type: "error",
                    text: "resendConfirmation.messages.notFound",
                });
            } else {
                setMessage({
                    type: "error",
                    text: "resendConfirmation.messages.error",
                });
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <PageTitle
                pageKey="resendConfirmation"
                titleKey="pageTitles.resendConfirmation"
                fallbackTitle={t("pageTitles.resendConfirmation")}
            />
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    background: "#E8EDF6",
                    py: { xs: 2, md: 0 },
                }}
            >
                <PageHeaderWithBreadcrumbs
                    breadcrumbs={[
                        { text: t("resendConfirmation.breadcrumbs.home"), href: "/" },
                        { text: t("resendConfirmation.breadcrumbs.resend") },
                    ]}
                    title={t("resendConfirmation.header.title")}
                    subtitle={t("resendConfirmation.header.subtitle")}
                    applyButtonText={false}
                    sticky={false}
                />
                <Container maxWidth="lg" >
                    <Box
                        sx={{
                            background: "#fff",
                            borderRadius: "8px",
                            boxShadow: "0 2px 16px rgba(24,59,86,0.07)",
                            p: { xs: 3, md: 4 },
                            // pr: { xs: 3, md: 0 },
                            // my: 0,
                        }}
                    >
                        <Typography
                            variant="h2"
                            sx={{
                                mb: 2,
                                color: "#183B56",
                                fontWeight: "bold",
                                fontSize: "30px"
                            }}
                        >
                            {t("resendConfirmation.titleForm")}
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                mb: 1,
                                color: "#183B56",
                                lineHeight: 1.6,
                            }}
                        >
                            {t("resendConfirmation.description")}
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                mb: 3,
                                color: "#183B56",
                                lineHeight: 1.6,
                            }}
                        >
                            {t("resendConfirmation.description2")}
                        </Typography>
                        {message.text && (
                            <Alert
                                severity={message.type}
                                sx={{ mb: 3 }}
                                onClose={() => setMessage({ type: "", text: "" })}
                            >
                                {t(message.text)}
                            </Alert>
                        )}

                        <Box sx={{
                            display: 'flex', gap: 0, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'stretch'
                        }}>
                            {/* Form Section - Left Side */}
                            <Box sx={{ flex: 2 }}>
                                <form onSubmit={handleSubmit}>
                                    {/* Passport Number Field */}
                                    <Box sx={{ mb: 3, maxWidth: { xs: '100%', md: '50%' } }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                            <label htmlFor="passportNumber">
                                                {t("resendConfirmation.form.passportNumber")} <span style={{ color: "red" }}>*</span>
                                            </label>
                                            <IconButton
                                                size="small"
                                                onClick={handleOpenDialog}
                                                aria-haspopup="dialog"
                                                aria-label={t("resendConfirmation.form.passportPopup.title")}
                                                sx={{
                                                    padding: 0.5,
                                                    color: '#1976d2',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                                    }
                                                }}
                                            >
                                                <HelpOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                        <TextField
                                            required
                                            aria-required="true"
                                            fullWidth
                                            name="passportNumber"
                                            value={formData.passportNumber}
                                            onChange={handleChange}
                                            error={!!errors.passportNumber}
                                            helperText={errors.passportNumber ? t(errors.passportNumber) : ""}
                                            placeholder={t(
                                                "resendConfirmation.form.passportNumberPlaceholder"
                                            )}
                                            disabled={loading}
                                        />
                                    </Box>

                                    {/* Passport Country Field */}
                                    <Box sx={{ mb: 3, maxWidth: { xs: '100%', md: '50%' } }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                            <label htmlFor="passportCountry">
                                                {t("resendConfirmation.form.passportCountry")} <span style={{ color: "red" }}>*</span>
                                            </label>
                                            <IconButton
                                                size="small"
                                                onClick={handleOpenCountryDialog}
                                                aria-haspopup="dialog"
                                                aria-label={t("resendConfirmation.form.countryPopup.title")}
                                                sx={{
                                                    padding: 0.5,
                                                    color: '#1976d2',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                                    }
                                                }}
                                            >
                                                <HelpOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                        <TextField
                                            required
                                            fullWidth
                                            select
                                            aria-required="true"
                                            name="passportCountry"
                                            value={formData.passportCountry}
                                            onChange={handleChange}
                                            error={!!errors.passportCountry}
                                            helperText={errors.passportCountry ? t(errors.passportCountry) : ""}
                                            disabled={loading}
                                            SelectProps={{
                                                displayEmpty: true,
                                                renderValue: (selected) => {
                                                    if (countriesLoading) {
                                                        return <span style={{ color: 'rgba(0, 0, 0, 0.38)' }}>{t("resendConfirmation.messages.loadingData")}</span>;
                                                    }
                                                    if (!selected) {
                                                        return <span style={{ color: 'rgba(0, 0, 0, 0.38)' }}>{t("resendConfirmation.form.passportCountryPlaceholder")}</span>;
                                                    }
                                                    const country = countries.find(c => c.COUNTRY_CODE === selected);
                                                    return country ? `${country.COUNTRY_CODE} - ${country.Official_country_name}` : selected;
                                                },
                                                MenuProps: {
                                                    PaperProps: {
                                                        style: {
                                                            maxHeight: 300,
                                                        },
                                                    },
                                                },
                                            }}
                                        >
                                            {countriesLoading ? (
                                                <MenuItem disabled value="">
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <CircularProgress size={20} />
                                                        {t("resendConfirmation.messages.loadingData")}
                                                    </Box>
                                                </MenuItem>
                                            ) : (
                                                countries
                                                    .filter((country) => country.passport_country !== "N" && country.ccc !== "Y" && country.EXT_COUNTRY_ID !== 900)
                                                    .sort((a, b) => a.COUNTRY_CODE.localeCompare(b.COUNTRY_CODE))
                                                    .map((country, index) => (
                                                        <MenuItem
                                                            key={`${country.COUNTRY_CODE}-${index}`}
                                                            value={country.COUNTRY_CODE}
                                                        >
                                                            {country.COUNTRY_CODE} - {country.Official_country_name}
                                                        </MenuItem>
                                                    ))
                                            )}
                                        </TextField>
                                    </Box>

                                    {/* Birth Date Field */}
                                    <Box sx={{ mb: 3, maxWidth: { xs: '100%', md: '50%' } }}>
                                        <Box sx={{ mb: 0.5 }}>
                                            <label htmlFor="birthDate">{t("resendConfirmation.form.birthDate")} <span style={{ color: "red", display: "inline" }}>*</span></label>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                            <TextField
                                            required
                                            aria-required="true"
                                                name="birthDay"
                                                value={formData.birthDay}
                                                onChange={(e) => handleDateChange(e, monthRef)}
                                                error={!!errors.birthDay}
                                                placeholder="DD"
                                                inputProps={{
                                                    maxLength: 2,
                                                    inputMode: 'numeric',
                                                    pattern: '[0-9]*'
                                                }}
                                                sx={{ flex: 1 }}
                                                disabled={loading}
                                            />
                                            <TextField
                                                required
                                                aria-required="true"
                                                inputRef={monthRef}
                                                name="birthMonth"
                                                value={formData.birthMonth}
                                                onChange={(e) => handleDateChange(e, yearRef)}
                                                error={!!errors.birthMonth}
                                                placeholder="MM"
                                                inputProps={{
                                                    maxLength: 2,
                                                    inputMode: 'numeric',
                                                    pattern: '[0-9]*'
                                                }}
                                                sx={{ flex: 1 }}
                                                disabled={loading}
                                            />
                                            <TextField
                                                required
                                                aria-required="true"
                                                inputRef={yearRef}
                                                name="birthYear"
                                                value={formData.birthYear}
                                                onChange={(e) => handleDateChange(e, null)}
                                                error={!!errors.birthYear}
                                                placeholder="YYYY"
                                                inputProps={{
                                                    maxLength: 4,
                                                    inputMode: 'numeric',
                                                    pattern: '[0-9]*'
                                                }}
                                                sx={{ flex: 2 }}
                                                disabled={loading}
                                            />
                                        </Box>
                                    </Box>
                                    {/* Common error zone for all date fields */}
                                    {(errors.birthDay || errors.birthMonth || errors.birthYear || errors.birthDate) && (
                                        <Box sx={{
                                            mt: 0.5,
                                            mb: 3,
                                            maxWidth: { xs: '100%', md: '50%' },
                                            color: '#d32f2f',
                                            fontSize: '0.75rem',
                                            lineHeight: 1.66,
                                            letterSpacing: '0.03333em',
                                            textAlign: 'left',
                                            marginLeft: '14px'
                                        }}>
                                            {errors.birthDay && <div>{t(errors.birthDay)}</div>}
                                            {errors.birthMonth && <div>{t(errors.birthMonth)}</div>}
                                            {errors.birthYear && <div>{t(errors.birthYear)}</div>}
                                            {errors.birthDate && <div>{t(errors.birthDate)}</div>}
                                        </Box>
                                    )}
                                    {!(errors.birthDay || errors.birthMonth || errors.birthYear || errors.birthDate) && <Box sx={{ mb: 0 }} />}

                                    <Button
                                        type="submit"
                                        variant="contained"
                                        // fullWidth
                                        disabled={loading || !isFormValid()}
                                        sx={{
                                            backgroundColor: "#183B56",
                                            color: "#fff",
                                            py: 1.5,
                                            fontSize: "1rem",
                                            fontWeight: 600,
                                            textTransform: "none",
                                            borderRadius: "8px",
                                            "&:hover": {
                                                backgroundColor: "#0F2942",
                                            },
                                            "&:disabled": {
                                                backgroundColor: "#ccc",
                                            },
                                        }}
                                    >
                                        {loading ? (
                                            <>
                                                <CircularProgress size={20} sx={{ mr: 1, color: "#fff" }} />
                                                {t("resendConfirmation.messages.loading")}
                                            </>
                                        ) : (
                                            t("resendConfirmation.form.submitButton")
                                        )}
                                    </Button>
                                </form>
                            </Box>

                            {/* Passport Guide Image - Right Side */}
                            <Box
                                sx={{
                                    flex: 1,
                                    display: { xs: 'none', md: 'flex' },

                                    justifyContent: 'flex-end',


                                    p: 3,
                                    pr: 0,
                                    // pr: { xs: 0, md: 3 },
                                    // borderLeft: '2px solid #E0E0E0',
                                    // position: 'relative',
                                    // background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                                    borderRadius: '0 8px 8px 0',
                                }}
                            >
                                <Box
                                    component="img"
                                    src={`${process.env.PUBLIC_URL}/email-illustration.png`}

                                    sx={{
                                        maxWidth: '350px',
                                        width: '100%',
                                        borderRadius: '8px',
                                        height: 'auto',
                                        opacity: 0.9,
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Container>
                <Space size="small" />
            </Box>

            {/* Passport Guide Dialog */}
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '6px',
                        border: '1px solid #E5E9F2',
                        padding: '16px',
                        backgroundColor: '#e8edf6',
                    }
                }}
            >
                <DialogContent sx={{ p: 2 }}>
                    <Typography
                        variant="h2"
                        sx={{
                            color: '#183B56',
                            fontWeight: 'bold',
                            mb: 1,
                            fontSize: '1.5rem'
                        }}
                    >
                        {t("resendConfirmation.form.passportPopup.title")}
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: '#183B56',
                            mb: 3,
                            lineHeight: 1.5
                        }}
                    >
                        {t("resendConfirmation.form.passportPopup.subtitle")}
                    </Typography>
                    <Box
                        component="img"
                        src={`${process.env.PUBLIC_URL}/svg/passportNum.svg`}
                        alt=""
                        sx={{
                            width: '100%',
                            height: 'auto',
                            display: 'block',
                            mb: 2,
                            border: '1px solid #E5E9F2',
                            borderRadius: '4px',
                            backgroundColor: '#e8edf6'
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'flex-start', px: 4, pb: 3 }}>
                    <Button
                        onClick={handleCloseDialog}
                        variant="outlined"
                        sx={{
                            color: '#183B56',
                            borderColor: '#183B56',
                            textTransform: 'none',
                            px: 3,
                            '&:hover': {
                                borderColor: '#183B56',
                                backgroundColor: 'rgba(24, 59, 86, 0.04)',
                            }
                        }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Country of Issue Guide Dialog */}
            <Dialog
                open={openCountryDialog}
                onClose={handleCloseCountryDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '6px',
                        border: '1px solid #E5E9F2',
                        padding: '16px',
                        backgroundColor: '#e8edf6',
                    }
                }}
            >
                <DialogContent sx={{ p: 2 }}>
                    <Typography
                        variant="h2"
                        sx={{
                            color: '#183B56',
                            fontWeight: 'bold',
                            mb: 1,
                            fontSize: '1.5rem'
                        }}
                    >
                        {t("resendConfirmation.form.countryPopup.title")}
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: '#183B56',
                            mb: 3,
                            lineHeight: 1.5
                        }}
                    >
                        {t("resendConfirmation.form.countryPopup.subtitle")}
                    </Typography>
                    <Box
                        component="img"
                        src={`${process.env.PUBLIC_URL}/svg/countryOfIssue.svg`}
                        alt=""
                        sx={{
                            width: '100%',
                            height: 'auto',
                            display: 'block',
                            mb: 2,
                            border: '1px solid #E5E9F2',
                            borderRadius: '4px',
                            backgroundColor: '#e8edf6'
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'flex-start', px: 4, pb: 3 }}>
                    <Button
                        onClick={handleCloseCountryDialog}
                        variant="outlined"
                        sx={{
                            color: '#183B56',
                            borderColor: '#183B56',
                            textTransform: 'none',
                            px: 3,
                            '&:hover': {
                                borderColor: '#183B56',
                                backgroundColor: 'rgba(24, 59, 86, 0.04)',
                            }
                        }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ResendConfirmation;
